/**
 * EventCard (docs/11 §4): the Next up card and the time-rail row.
 *
 *  next-up        one imminent, reachable opportunity: 56 px leave-by countdown, amber CTA
 *  next-up-later  the next thing, hours away: clock time instead of minutes, calm CTA
 *  listed         a rail row: exit time on the rail, leave time in teal
 *  unreachable    a rail row the driver can't make: muted, says so in words
 *
 * The countdown ticks once a minute (parent passes `now` from useMinuteTick);
 * nothing here animates.
 */
import {
  Zap, Clock, CarFront, MapPin, ChevronRight, Bell, Volume2, CalendarDays, Database,
} from 'lucide-react';
import { CrowdMeter, CrowdDial } from './CrowdMeter.js';
import { LiveNow, LiveTag } from './LiveNow.js';
import type { Opp } from './api.js';
import { Button } from './ui.js';
import {
  countdown, fmtTime, fmtWindow, demandShort, headingShort,
  fmtDistance, dayPrefix, fmtEventWhen, sourceParts, type CountdownState,
} from './format.js';

export type CardVariant = 'next-up' | 'next-up-later' | 'listed' | 'unreachable';

export function Countdown({ target, now, size = 'display' }: {
  target: string; now: number; size?: 'display' | 'hero';
}) {
  const c = countdown(target, now);
  const cls = size === 'display' ? 'display' : 'hero-num';
  return <div className={`${cls} ${c.state}`} aria-live="off">{c.label}</div>;
}

export function countdownState(target: string | null, now: number): CountdownState | 'unknown' {
  return target ? countdown(target, now).state : 'unknown';
}

export function NextUpCard({ opp, now, units, hasOrigin, tripActive, onHeadThere, onDetails, onRemind, reminded, onReadAgain }: {
  opp: Opp;
  now: number;
  units: 'mi' | 'km';
  hasOrigin: boolean;
  tripActive: boolean;
  onHeadThere: () => void;
  onDetails: () => void;
  onRemind?: () => void;
  reminded?: boolean;
  onReadAgain: () => void;
}) {
  const venue = opp.occurrence.venue_name ?? opp.occurrence.title;
  const leave = opp.leave_by ? countdown(opp.leave_by, now) : null;
  const later = !leave || leave.minutes > 60;
  const day = dayPrefix(opp.egress.p10_at, now);

  return (
    <section className="card next" aria-label={later ? 'Next up, later' : 'Next up'}>
      <span className="overline amber">
        <Zap size={16} aria-hidden="true" />
        {later ? `Next up${leave ? ` · in ${leave.label}` : day ? ` · ${day}` : ''}` : 'Next up'}
      </span>

      {leave ? (
        later ? (
          <>
            <span className="label">Leave around</span>
            <div className="display num">{fmtTime(opp.leave_by!)}</div>
          </>
        ) : (
          <>
            <span className="label">Leave in</span>
            <Countdown target={opp.leave_by!} now={now} />
            <span className="sub num">by {fmtTime(opp.leave_by!)}</span>
          </>
        )
      ) : (
        <>
          <span className="label">Crowd comes out</span>
          <div className="display num">{fmtTime(opp.egress.p10_at)}</div>
          <span className="sub">{hasOrigin ? 'Too far to make this one' : 'Turn on location for a leave-by time'}</span>
        </>
      )}

      <h2 className="title" style={{ marginTop: 6 }}>{venue}</h2>
      {opp.occurrence.venue_name && <p className="sub">{opp.occurrence.title}</p>}
      <div className="meta">
        <span className="pair num wrap"><CalendarDays size={18} aria-hidden="true" /><span className="wrap">{fmtEventWhen(opp.occurrence.starts_at, now)}</span></span>
      </div>
      <div className="meta">
        <span className="pair num"><Clock size={18} aria-hidden="true" />Crowd out {fmtWindow(opp.egress.p10_at, opp.egress.p90_at)}{day ? ` · ${day}` : ''}</span>
      </div>
      <LiveNow opp={opp} now={now} compact />
      <CrowdMeter opp={opp} size="md" />
      <div className="meta">
        <span className="pair"><CarFront size={18} aria-hidden="true" />{headingShort(opp.heading)}</span>
      </div>
      <div className="meta">
        <span className="pair wrap"><MapPin size={18} aria-hidden="true" /><span className="wrap">Pickup: <b style={{ color: 'var(--text-1)', fontWeight: 600 }}>{opp.staging.label}</b></span></span>
      </div>

      <Sources opp={opp} />

      <div className="card-actions">
        {later ? (
          <div className="btn-row">
            {onRemind && leave && (
              <Button variant="secondary" icon={<Bell size={18} aria-hidden="true" />}
                onClick={onRemind} disabled={reminded}>
                {reminded ? 'Reminder set' : `Remind me at ${fmtTime(opp.leave_by!)}`}
              </Button>
            )}
            <Button variant="secondary" onClick={onDetails}>Details</Button>
          </div>
        ) : (
          <>
            <Button variant="primary" onClick={onHeadThere}
              icon={<ChevronRight size={20} aria-hidden="true" />}>
              {tripActive ? 'Switch to this' : 'Head there'}
            </Button>
            <div className="btn-row">
              <Button variant="text" onClick={onDetails}>Details</Button>
              <Button variant="quiet" className="end" onClick={onReadAgain}
                icon={<Volume2 size={18} aria-hidden="true" />} aria-label="Read the next move again">
                Read again
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/** One quiet line saying where each number came from. */
export function Sources({ opp }: { opp: Opp }) {
  const s = sourceParts(opp);
  return (
    <p className="sources">
      <Database size={15} aria-hidden="true" />
      <span>Event from {s.event} · crowd from {s.crowd} · exit time from {s.exit}</span>
    </p>
  );
}

export function EventRow({ opp, now, units, hasOrigin, selected, hideDay, onOpen }: {
  opp: Opp; now: number; units: 'mi' | 'km'; hasOrigin: boolean; selected?: boolean;
  /** Inside a "Tomorrow" group the header already says so. */
  hideDay?: boolean; onOpen: () => void;
}) {
  const venue = opp.occurrence.venue_name ?? opp.occurrence.title;
  const muted = hasOrigin && !opp.reachable;
  const leave = opp.leave_by ? countdown(opp.leave_by, now) : null;
  const day = hideDay ? '' : dayPrefix(opp.egress.p10_at, now);
  return (
    <button className={`row${muted ? ' muted' : ''}${selected ? ' selected' : ''}`} onClick={onOpen}
      aria-label={`${venue}, ${opp.occurrence.title}, crowd out ${fmtWindow(opp.egress.p10_at, opp.egress.p90_at)}`}>
      <span className="when">
        <span className="day">Crowd out</span>
        <span className="num">{fmtTime(opp.egress.p10_at)}</span>
        {day && <span className="day">{day}</span>}
        <span className="mini"><CrowdDial level={opp.crowd.level} size="sm" /></span>
      </span>
      <span className="main">
        <span className="venue">{opp.live?.state === 'in' && <LiveTag />}{venue}</span>
        <span className="what num">{fmtEventWhen(opp.occurrence.starts_at, now)}</span>
        <span className="what">
          {/* Size and distance first: the title is what may truncate. */}
          {demandShort(opp.demand_band)} · {fmtDistance(opp.distance_km, units)}
          {opp.occurrence.venue_name ? ` · ${opp.occurrence.title}` : ''}
        </span>
        <span className={`leave${muted ? ' cant' : ''}`}>
          {!hasOrigin ? '' : muted ? 'Can’t make this one'
            : leave ? (leave.state === 'now' || leave.state === 'passed' ? leave.label : `Leave ${fmtTime(opp.leave_by!)}`) : ''}
        </span>
        <span className="src">Source: {opp.occurrence.source}</span>
      </span>
      <ChevronRight className="chev" size={20} aria-hidden="true" />
    </button>
  );
}
